import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { Input, Textarea, Label, Button } from "@/components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PersonAvatar } from "@/components/ui/person-avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getTeacherById } from "@/services/admin/teacher.service";
import { Teacher } from "@/services/admin/teacher.interfaces";
import { Badge } from "@/components/ui/badge"; // Import Badge

const EditTeacher: React.FC = () => {
  const { teacherId } = useParams();
  const [teacherData, setTeacherData] = useState<Teacher | null>(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [removeLinkedStudentDialogOpen1, setRemoveLinkedStudentDialogOpen1] = useState(false);
  const [removeLinkedStudentDialogOpen2, setRemoveLinkedStudentDialogOpen2] = useState(false);

  // Novo estado para armazenar o filtro de linked students
  const [studentFilter, setStudentFilter] = useState("");

  // Lista simulada de linked students com propriedade 'gender' e 'status'
  const linkedStudents = [
    {
      id: "student1",
      name: "Alice Johnson",
      gender: "women",
      status: "Ativo",
      onRemove: () => setRemoveLinkedStudentDialogOpen1(true),
    },
    {
      id: "student2",
      name: "Bob Smith",
      gender: "men",
      status: "Inativo",
      onRemove: () => setRemoveLinkedStudentDialogOpen2(true),
    },
  ];

  // Filtra os estudantes com base no termo do filtro (case insensitive)
  const filteredStudents = linkedStudents.filter((student) =>
    student.name.toLowerCase().includes(studentFilter.toLowerCase())
  );

  useEffect(() => {
    const fetchTeacherData = async () => {
      if (teacherId) {
        try {
          const data = await getTeacherById(teacherId);
          setTeacherData(data);
        } catch (error) {
          console.error("Error fetching teacher data:", error);
        }
      }
    };

    fetchTeacherData();
  }, [teacherId]);

  if (!teacherData) {
    return <div>Loading teacher data...</div>;
  }

  return (
    <AdminPageLayout title={`Edit Teacher ${teacherId}`}>
      <main id="main-content" className="mx-auto py-8">
        <div id="profile-header" className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <PersonAvatar
              size="xl"
              userId={teacherId}
              userName={teacherData.name}
              avatarUrl={teacherData.avatar?.url}
            />
            <div>
              <h1 className="text-2xl font-semibold">Edit Teacher Profile</h1>
              <p className="text-neutral-600">ID: {teacherData.id}</p>
            </div>
          </div>
        </div>
        <div id="form-container" className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="full-name">Full Name</Label>
              <Input id="full-name" type="text" defaultValue={teacherData.name} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" defaultValue={teacherData.email} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => setResetPasswordDialogOpen(true)}
              >
                Reset Password
              </Button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select defaultValue={teacherData.status || "Active"}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="registration-date">Registration Date</Label>
            <Input
              id="registration-date"
              type="text"
              defaultValue={teacherData.enrollmentDate}
              readOnly
              className="bg-neutral-50"
            />
          </div>
          {/* Bloco de Linked Students com filtro */}
          <div id="linked-students" className="space-y-4">
            <div className="flex justify-between items-center">
              <Label htmlFor="students">Linked Students</Label>
              <Button variant="ghost" className="text-sm">
                View All
              </Button>
            </div>
            {/* Input de filtro */}
            <div className="mb-2">
              <Input
                type="text"
                placeholder="Filter linked students..."
                value={studentFilter}
                onChange={(e) => setStudentFilter(e.target.value)}
              />
            </div>
            <div className="border rounded-lg p-4 space-y-3">
              {filteredStudents.map((student) => (
                <div key={student.id} className="flex items-center justify-between py-2">
                  <div className="flex items-center space-x-3">
                    <img
                      src={`https://randomuser.me/api/portraits/${student.gender}/${Math.floor(Math.random() * 100)}.jpg`}
                      className="w-8 h-8 rounded-full"
                      alt="Student avatar"
                    />
                    <div>
                      <span>{student.name}</span>
                      <br />
                      <Badge variant={student.status === "Ativo" ? "success" : "destructive"}>
                        {student.status}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={student.onRemove}
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </Button>
                </div>
              ))}
              {filteredStudents.length === 0 && (
                <p className="text-sm text-neutral-500">No linked students found.</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" rows={20} defaultValue="" />
          </div>
        </div>
        <div id="actions" className="mt-8 flex items-center justify-between pt-6 border-t">
          <Button variant="outline">Cancel</Button>
          <div className="flex space-x-4">
            <Button variant="ghost" className="text-neutral-600">
              Remove Teacher
            </Button>
            <Button variant="default">Save Changes</Button>
          </div>
        </div>
        {/* Reset Password Confirmation Dialog */}
        <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Are you sure you want to reset the password for this teacher? They will receive an email with a temporary password.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  // Handle reset password logic here
                  setResetPasswordDialogOpen(false);
                }}
              >
                Reset Password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Remove Linked Student Confirmation Dialog - Alice Johnson */}
        <Dialog open={removeLinkedStudentDialogOpen1} onOpenChange={setRemoveLinkedStudentDialogOpen1}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Remove Linked Student</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove Alice Johnson from linked students? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRemoveLinkedStudentDialogOpen1(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  // Handle remove linked student logic here for Alice Johnson
                  setRemoveLinkedStudentDialogOpen1(false);
                }}
              >
                Confirm Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Remove Linked Student Confirmation Dialog - Bob Smith */}
        <Dialog open={removeLinkedStudentDialogOpen2} onOpenChange={setRemoveLinkedStudentDialogOpen2}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Remove Linked Student</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove Bob Smith from linked students? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRemoveLinkedStudentDialogOpen2(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  // Handle remove linked student logic here for Bob Smith
                  setRemoveLinkedStudentDialogOpen2(false);
                }}
              >
                Confirm Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </AdminPageLayout>
  );
};

export default EditTeacher;
